<script setup>
import { defineEmits, onUnmounted, watch } from "vue";

// Define component props
const props = defineProps({
    showModal: {
        type: Boolean,
        default: false,
    },
    title: {
        type: String,
        default: "",
    },
    text: {
        type: String,
        default: "",
    },
});

// Define custom events
const emit = defineEmits(["close-modal"]);

// Function to close the modal
const closeModal = () => {
    // eslint-disable-next-line no-use-before-define
    document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line no-use-before-define
    document.removeEventListener("click", handleOutsideClick);
    emit("close-modal");
};

// Function to handle keydown event
const handleKeyDown = (event) => {
    if (event.key === "Escape") {
        closeModal();
    }
};

// Function to handle outside click event
const handleOutsideClick = (event) => {
    if (!event.target.closest(".modal-content")
        && !event.target.classList.contains("modal-handler")) {
        closeModal();
    }
};

// Watch for changes in showModal prop
watch(() => props.showModal, (val) => {
    if (val === true) {
        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("click", handleOutsideClick);
    }
});

// Cleanup event listeners when component is unmounted
onUnmounted(() => {
    document.removeEventListener("keydown", handleKeyDown);
    document.removeEventListener("click", handleOutsideClick);
});
</script>

<template>
    <div>
        <div
            class="modal"
            data-test="modal"
            :class="{ 'modal-open d-block': showModal }"
            tabindex="-1"
            role="dialog"
            aria-hidden="true"
        >
            <div
                class="modal-dialog modal-lg"
                role="document"
            >
                <div
                    class="modal-content"
                    data-test="modal-content"
                >
                    <div
                        class="modal-header p-3"
                        data-test="modal-header"
                    >
                        <div class="modal-title" v-html="title"></div>
                        <button
                            type="button"
                            data-test="modal-btn-close"
                            class="close close-button"
                            @click="closeModal"
                            aria-label="Close"
                        >
                            <span class="" aria-hidden="true"
                            >&times;</span
                            >
                        </button>
                    </div>
                    <div class="hr"></div>
                    <div class="modal-body p-3" data-test="modal-body">
                        <div>
                            {{ text }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="modal-backdrop"
            :class="{ show: showModal, 'modal-backdrop-show': showModal }"
        ></div>
    </div>
</template>

<style scoped>

.btn-close {
    padding: 14px 24px;
    text-decoration: none;
}

.modal-backdrop {
    display: none;
}

.modal-backdrop-show {
    display: block !important;
}

.modal.modal-open .modal-dialog {
    z-index: 10;
}

.modal {
    overflow-x: hidden;
    overflow-y: auto;
}

.modal-content:not(.remove-modal) .close-button {
    position: absolute;
    right: 1rem;
}

.close-button {
    z-index: 3;
}

.modal-header_title {
    font-style: normal;
    font-weight: 600;
    font-size: 32px;
    line-height: 48px;
    color: #260045;
}

.modal-header_recurrence {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 21px;
    color: #999999;
}

.modal-header_time {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 21px;
    color: #636066;
}

.grey-header {
    background: rgba(221, 221, 221, 0.5);
    margin: -3rem -3rem 0;
    padding: 1.5rem 3rem !important;
}

.simple-header {
    cursor: pointer;
    position: absolute;
    top: 28px;
    right: 25px;
    z-index: 5;
}

.modal-content:not(.remove-modal) .modal-header {
    border-bottom: none;
}

.modal-content:not(.remove-modal) .modal-footer {
    border-top: none;
}

.modal-footer {
    margin-top: 3rem;
}

.modal-content {
    max-height: 500px;
}

.modal-header,
.modal-body,
.modal-footer {
    padding: unset;
}

.modal-body {
    overflow-y: scroll;
    font-size: 14px;
}

.close.close-button {
    opacity: 1;
}

.close.close-button:hover {
    opacity: 1;
}

.modal-title {
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 27px;
    color: #272529;
}

.hr {
    border-bottom: 1px solid #eeeeee;
}

</style>
